/**
 * Bausteine der Monteur-App.
 *
 * **Sie sind nicht dieselben wie im Portal**, und das ist kein Versäumnis.
 * `07` §7.14 verlangt für die App große Touchziele, Bedienung mit einer Hand,
 * hohen Kontrast und robuste Bedienung mit Handschuhen. Eine Zeile, die auf
 * einem Bürobildschirm gut aussieht, ist auf einem Handydisplay im Sonnenlicht
 * mit nassen Fingern etwas anderes. Geteilt werden die **Token** — die Farben,
 * Abstände und Trefferflächen kommen aus `@hwc/ui`, aus derselben Quelle wie
 * im Portal (ADR-040).
 *
 * Was geteilt bleibt, ist die Bedeutung: Ein Einsatz im Zustand `wartet` heißt
 * hier wie dort „Wartet", und die Farbe ist auch hier nur die Zugabe.
 */

import type { ReactNode } from "react";

import type { Anschrift } from "@hwc/demo-data";
import type { EinsatzZustand } from "@hwc/sync";

const ZUSTANDSTEXT: Record<EinsatzZustand, string> = {
  geplant: "Geplant",
  anfahrt: "Anfahrt",
  "vor-ort": "Vor Ort",
  wartet: "Wartet",
  abschluss: "Abschluss",
  uebermittelt: "Übermittelt",
  geprueft: "Geprüft",
  storniert: "Storniert",
};

const ZUSTANDSTON: Record<EinsatzZustand, string> = {
  geplant: "neutral",
  anfahrt: "laeuft",
  "vor-ort": "laeuft",
  wartet: "warnung",
  abschluss: "warnung",
  uebermittelt: "warnung",
  geprueft: "erfolg",
  storniert: "aus",
};

export function Einsatzabzeichen({ zustand }: { zustand: EinsatzZustand }) {
  return (
    <span className={`abzeichen abzeichen--${ZUSTANDSTON[zustand]}`}>
      {ZUSTANDSTEXT[zustand]}
    </span>
  );
}

export function zustandText(zustand: EinsatzZustand): string {
  return ZUSTANDSTEXT[zustand];
}

/**
 * Die Anzeige „wartet auf Upload" — einer der fünf Zustände aus `07` §7.9.
 *
 * Sie steht an der Sache, nicht nur in einer Gesamtübersicht. Ein Monteur,
 * der wissen will, ob seine Zeit angekommen ist, sieht sonst nur eine Zahl in
 * der Kopfzeile und muss raten, welche Buchung sie meint.
 */
export function Wartemarke({ anzahl }: { anzahl: number }) {
  if (anzahl <= 0) return null;
  return (
    <span className="abzeichen abzeichen--warnung">
      {anzahl} wartet auf Upload
    </span>
  );
}

export function Ortszeile({ anschrift }: { anschrift: Anschrift }) {
  return (
    <p className="ortszeile">
      {anschrift.strasse}, {anschrift.plz} {anschrift.ort}
    </p>
  );
}

export function Leer({ text }: { text: string }) {
  return <p className="leer leise">{text}</p>;
}

/**
 * Eine Reihe großer Knöpfe.
 *
 * `07` §7.14 nennt „wichtige Aktionen nicht in versteckten Gesten". Was hier
 * steht, sind die Aktionen des Einsatzablaufs — sie gehören sichtbar
 * untereinander und nicht hinter ein Wischen oder ein Dreipunktmenü.
 */
export function Knopfleiste({ children }: { children: ReactNode }) {
  return <div className="knopfleiste">{children}</div>;
}

export function Grossknopf({
  children,
  onClick,
  art = "neben",
  disabled = false,
}: {
  children: ReactNode;
  onClick: () => void;
  art?: "haupt" | "neben" | "gefahr";
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={`knopfleiste__knopf knopfleiste__knopf--${art}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

// ───────────────────────── Formatierung ─────────────────────────

const UHRZEIT = new Intl.DateTimeFormat("de-DE", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Berlin",
});

const DATUM = new Intl.DateTimeFormat("de-DE", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
  timeZone: "Europe/Berlin",
});

export function uhrzeit(iso: string): string {
  return UHRZEIT.format(new Date(iso));
}

export function datum(iso: string): string {
  return DATUM.format(new Date(iso.length === 10 ? `${iso}T12:00:00` : iso));
}

export function zeitfenster(von: string, bis: string): string {
  return `${uhrzeit(von)}–${uhrzeit(bis)}`;
}

/**
 * Minuten als Stunden und Minuten — wie im Portal.
 *
 * `2:45 h` und nicht `2,75 h`. Der Monteur hat zwei Stunden und
 * fünfundvierzig Minuten gearbeitet; Dezimalstunden rechnet niemand im Kopf
 * zurück, und auf einer Baustelle schon gar nicht.
 */
export function dauer(minuten: number): string {
  const h = Math.floor(minuten / 60);
  const m = minuten % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")} h` : `${m} min`;
}

export function groesse(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

/** Die aktuelle Uhrzeit als `HH:MM`, für die Vorbelegung von Zeitfeldern. */
export function jetztHHMM(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * `HH:MM` von heute als ISO-Zeitpunkt mit Zonenversatz.
 *
 * Der Versatz wird aus dem Datum selbst gelesen statt fest eingetragen: Eine
 * Zeitbuchung am Tag der Umstellung läge sonst um eine Stunde daneben, und
 * das an genau zwei Tagen im Jahr — der Sorte Fehler, die niemand
 * reproduziert.
 */
export function alsZeitpunkt(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date();
  d.setHours(h ?? 0, m ?? 0, 0, 0);

  const versatz = -d.getTimezoneOffset();
  const vz = versatz >= 0 ? "+" : "-";
  const va = Math.abs(versatz);
  const zz = (n: number) => String(n).padStart(2, "0");

  return (
    `${d.getFullYear()}-${zz(d.getMonth() + 1)}-${zz(d.getDate())}`
    + `T${zz(d.getHours())}:${zz(d.getMinutes())}:00`
    + `${vz}${zz(Math.floor(va / 60))}:${zz(va % 60)}`
  );
}
